// @flow
import React, { useCallback } from "react";
import CellMeasurer from "react-virtualized/dist/commonjs/CellMeasurer";
import ContextMenuHandler from "components/ContextMenu/Handler";
import { DocColumnResultDTO, DocListLayout } from "data/types";
import Icons from "@hs/icons";
import styles from "./RowRenderer.module.css";

interface RowRendererProps {
    index: number;
    key: string;
    parent: any;
    style: React.CSSProperties;
    isRowLoaded: (params: { index: number }) => boolean;
    rows: Object;
    cache: React.MutableRefObject<CellMeasurerCache>;
    refreshId: number;
    activeRowIndex: number;
    hoveredRowIndex: number;
    setHoveredRowIndex: (index: number) => void;
    onResize: () => void;
    RenderRowComponentClass: React.Node;
    cellOnContextMenu: (
        e: SyntheticMouseEvent,
        rowIndex: number,
        itemUri: string
    ) => void;
    showThumbnails: boolean;
    filterText: string;
    layout: DocListLayout;
    cols: DocColumnResultDTO[];
    onIconClick: (e: SyntheticMouseEvent) => void;
    onSingleClick: (
        e: SyntheticMouseEvent,
        itemUri: string,
        index: number
    ) => void;
    onMoreClick: (
        e: SyntheticMouseEvent,
        itemUri: string,
        index: number
    ) => void;
    onDoubleClick: (e: SyntheticMouseEvent) => void;
    actionsDisabled: boolean;
}

const RowRenderer: React.FC<RowRendererProps> = ({
    index,
    key,
    parent,
    style,
    isRowLoaded,
    rows,
    cache,
    refreshId,
    activeRowIndex,
    hoveredRowIndex,
    setHoveredRowIndex,
    onResize,
    RenderRowComponentClass,
    cellOnContextMenu,
    showThumbnails,
    filterText,
    layout,
    cols,
    onIconClick,
    onSingleClick,
    onMoreClick,
    onDoubleClick,
    actionsDisabled,
}) => {
    if (isRowLoaded({ index })) {
        const row = rows[index];
        const itemUri = row[row.length - 1];

        const handleRowContextMenu = useCallback(
            (e: SyntheticMouseEvent) => {
                cellOnContextMenu(e, index, itemUri);
            },
            [cellOnContextMenu, index, itemUri]
        );

        const handleRowMouseEnter = useCallback(() => {
            setHoveredRowIndex(index);
        }, [setHoveredRowIndex, index]);

        const handleRowMoreClick = useCallback(
            (e: SyntheticMouseEvent) => {
                /* istanbul ignore else */
                if (typeof onMoreClick === "function") {
                    onMoreClick(e, itemUri, index);
                }
            },
            [onMoreClick, itemUri, index]
        );

        return (
            <CellMeasurer
                cache={cache}
                columnIndex={0}
                key={key}
                rowIndex={index}
                parent={parent}
                refreshId={refreshId}
            >
                {({ measure }) => (
                    <ContextMenuHandler
                        componentClass="div"
                        onContextMenu={handleRowContextMenu}
                        onMouseEnter={handleRowMouseEnter}
                        rowIndex={index}
                        columnIndex={0}
                        style={style}
                    >
                        {React.createElement(RenderRowComponentClass, {
                            index,
                            row,
                            onLoad: onResize,
                            showThumbnail: showThumbnails,
                            filterText,
                            layout,
                            cols,
                            isSelected: activeRowIndex === index,
                            isHovered: hoveredRowIndex === index,
                            onIconClick,
                            onSingleClick: (e: SyntheticMouseEvent) =>
                                onSingleClick(e, itemUri, index),
                            onMoreClick: handleRowMoreClick,
                            onDoubleClick,
                            actionsDisabled,
                        })}
                    </ContextMenuHandler>
                )}
            </CellMeasurer>
        );
    } else {
        return (
            <CellMeasurer
                cache={cache}
                columnIndex={0}
                key={key}
                rowIndex={index}
                parent={parent}
                refreshId={refreshId}
            >
                {({ measure }) => (
                    <div key={key} className={styles.placeholder} style={style}>
                        <Icons.Library name="spinner" className="fa-pulse" />
                    </div>
                )}
            </CellMeasurer>
        );
    }
};

export default React.memo(RowRenderer);
